import React, { useState, useEffect } from "react";
import DashboardSidebarMenu from "../../components/controls/DashboardSideBarMenu";
import { getProviderMenus } from "../../contexts/ProviderMenus";
import { useTranslation } from "react-i18next";
import ProductModal from "./ProductModal";
import ProductCard from "../../components/controls/ProductCard";
import { PlusIcon } from "@heroicons/react/24/solid";
import { getProductsByCompanyId } from "../../db/products";
import { fetchCompaniesByUserId } from "../../db/Companies";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const ProductsList = () => {
  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const { t } = useTranslation();
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const menus = getProviderMenus();
  const navigate = useNavigate();

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const data = await fetchCompaniesByUserId(userId);
        setCompanies(data);
        if (data.length > 0) {
          setSelectedCompanyId(data[0].id);
        }
      } catch (err) {
        console.error("Erreur lors du chargement des compagnies :", err);
        setError("Erreur lors du chargement des compagnies.");
      }
    };

    loadCompanies();
  }, [userId]);

  useEffect(() => {
    const loadProducts = async () => {
      if (!selectedCompanyId) return;
      try {
        setLoading(true);
        const data = await getProductsByCompanyId(selectedCompanyId);
        setProducts(data);
      } catch (err) {
        console.error("Erreur lors du chargement des produits :", err);
        setError("Une erreur est survenue lors du chargement des produits.");
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [selectedCompanyId]);

  const openEditModal = (data) => {
    navigate(`/provider-dashboard/products/${data.id}`);
  };

  const openAddModal = () => {
    navigate("/provider-dashboard/products/new?companyId=" + selectedCompanyId);
  };

  const closeModal = () => {
    setSelectedRowData(null);
    setIsModalOpen(false);
  };

  const saveProduct = (productData) => {
    setProducts((prev) => {
      if (prev.some((item) => item.id === productData.id)) {
        return prev.map((item) =>
          item.id === productData.id ? { ...item, ...productData } : item
        );
      }
      return [...prev, productData];
    });
    closeModal();
  };

  if (loading) {
    return <div>Chargement...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="flex min-h-screen text-primary">
      <DashboardSidebarMenu title={t("admin.title")} menus={menus} />
      <div className="flex-grow">
        <div className="container mx-auto px-6 py-8">
          <h1 className="text-2xl font-bold mb-4 mx-4 text-onWhite">
            {t("products.myProducts")}
          </h1>

          <div className="w-full max-w-sm mb-6 mx-4">
            <label className="block text-sm font-medium text-onWhite mb-1">
              {t("products.selectCompany")}
            </label>
            <select
              className="select select-bordered w-full bg-base-100 text-onWhite focus:outline-none focus:ring-2 focus:ring-primary"
              value={selectedCompanyId || ""}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div
            className="mb-4 bg-primary px-4 py-2 rounded-full mx-4 inline-flex items-center space-x-2 hover:cursor-pointer"
            onClick={openAddModal}
          >
            <PlusIcon className="w-6 h-6 text-white" />
            <button type="submit" className="text-white">
              {t("modal.addProduct")}
            </button>
          </div>

          {products.length === 0 ? (
            <p className="mx-4 text-gray-500 italic">
              {t("products.noProductForCompany") ||
                "Aucun produit pour cette compagnie."}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  openEditModal={() => openEditModal(product)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsList;
